/**
 * Web twin of `CreateCvSourceStep` - step 1: WHERE this CV's content comes from.
 *
 * "An existing CV" opens a picker dialog rather than unrolling the CV list under
 * the row: which CV is its own decision, and an inline list of every CV the user
 * owns pushes the button off screen. Once picked, the CV's name IS the row's
 * subtitle.
 *
 * It used to be inlined in `create.web.tsx`, which is why that route file ran to
 * three times its native twin and the two screens could quietly drift apart.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { useLocale } from '../../../hooks/useLocale';
import type { CvListItem } from '../../../lib/cv-main-default';
import { ResumeImportComposer } from '../ResumeImportComposer';
import type {
  ResumeComposerDraft,
  ResumeImportComposerParsed,
} from '../use-resume-import-composer';
import { CreateCvOptionCard } from './CreateCvOptionCard';
import { CvPickerSheet } from './CvPickerSheet';
import type { CreateCvSource } from './create-cv-machine';
import {
  HINT,
  INPUT_PILL,
  LINK_BUTTON,
  PARSED_ROW,
} from './create-cv-web-styles';

interface Props {
  source: CreateCvSource | null;
  onSourceChange: (source: CreateCvSource) => void;
  sourceCvId: string | null;
  onSourceCvChange: (cvId: string) => void;
  title: string;
  onTitleChange: (title: string) => void;
  /** True once an uploaded resume has been parsed and is ready to use. */
  resumeParsed: boolean;
  onResumeParsed: (result: ResumeImportComposerParsed) => void | Promise<void>;
  onResumeReset: () => void;
  /** Reports the composer's unsent draft so Continue can submit it. */
  onResumeDraftChange: (draft: ResumeComposerDraft) => void;
  onResumeBusyChange: (busy: boolean) => void;
  /** Filled with the composer's submit so Continue can call it. */
  composerSubmit: React.MutableRefObject<(() => void) | null>;
  cvs: CvListItem[];
  cvsLoading: boolean;
}

export function CreateCvSourceStep({
  source,
  onSourceChange,
  sourceCvId,
  onSourceCvChange,
  title,
  onTitleChange,
  resumeParsed,
  onResumeParsed,
  onResumeReset,
  onResumeDraftChange,
  onResumeBusyChange,
  composerSubmit,
  cvs,
  cvsLoading,
}: Props) {
  const { t } = useTranslation('seeker');
  const { isRTL } = useLocale();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);

  const existingSelected = source === 'existing';
  const selectedCv = cvs.find((cv) => cv.id === sourceCvId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* No "Start from" heading here: the wizard's progress header already
          names this step, and repeating it reads as two headings for one list. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <CreateCvOptionCard
          icon={<MaterialSymbol name="description" size={20} />}
          title={t('cv.create.source.existing')}
          subtitle={
            existingSelected && selectedCv
              ? selectedCv.title || t('cv.create.source.untitledCv')
              : t('cv.create.source.existingHint')
          }
          selected={existingSelected}
          accessibilityHint={t('cv.create.source.pickerHint')}
          trailing={
            <MaterialSymbol
              name={isRTL ? 'chevron_left' : 'chevron_right'}
              size={20}
              style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
            />
          }
          onPress={() => {
            if (!existingSelected) onSourceChange('existing');
            setPickerOpen(true);
          }}
        />

        <CreateCvOptionCard
          icon={<MaterialSymbol name="upload_file" size={20} />}
          title={t('cv.create.source.upload')}
          subtitle={t('cv.create.source.uploadHint')}
          selected={source === 'upload'}
          onPress={() => onSourceChange('upload')}
        >
          {/* Parsed here, in step 1, so step 2 tailors what the resume actually
              contained instead of tailoring a promise. */}
          {resumeParsed ? (
            <div style={PARSED_ROW}>
              <MaterialSymbol
                name="check_circle"
                size={16}
                style={{ color: 'var(--text-primary)' }}
              />
              <span style={{ flex: 1 }}>
                {t('cv.create.source.uploadReady')}
              </span>
              <button type="button" onClick={onResumeReset} style={LINK_BUTTON}>
                {t('cv.create.source.uploadReplace')}
              </button>
            </div>
          ) : (
            <ResumeImportComposer
              title={t('cv.tailored.uploadTitle')}
              subtitle={t('cv.tailored.uploadSubtitle')}
              placeholder={t('cv.tailored.uploadPlaceholder')}
              parseFailedTitle={t('cv.tailored.importFailedTitle')}
              parseFailedMessage={t('cv.tailored.importFailedMessage')}
              onParsed={onResumeParsed}
              showIntro={false}
              skipEnrichment
              analyticsContext="cv_create"
              // Attaching or dropping a CV IS the answer to "start from an
              // upload": start reading it, and let Continue take over.
              parseOnAttach
              onDraftChange={onResumeDraftChange}
              onBusyChange={onResumeBusyChange}
              submitRef={composerSubmit}
            />
          )}
        </CreateCvOptionCard>

        <CreateCvOptionCard
          icon={<MaterialSymbol name="add" size={20} />}
          title={t('cv.create.source.blank')}
          subtitle={t('cv.create.source.blankHint')}
          selected={source === 'blank'}
          onPress={() => onSourceChange('blank')}
        />
      </div>

      {/* The name is derived from the source (or the skill) when left blank,
          which is what the hint always promised - so it does not need a labeled
          block of its own between the options and the button. */}
      {nameOpen || title.trim() ? (
        <div>
          <input
            type="text"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder={t('cv.create.namePlaceholder')}
            autoFocus={nameOpen && !title.trim()}
            style={INPUT_PILL}
            dir="auto"
          />
          <p style={HINT}>{t('cv.create.nameHint')}</p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setNameOpen(true)}
          style={{
            ...LINK_BUTTON,
            alignSelf: 'flex-start',
            textDecoration: 'none',
            color: 'var(--text-secondary)',
          }}
        >
          {t('cv.create.nameToggle')}
        </button>
      )}

      <CvPickerSheet
        visible={pickerOpen}
        onClose={() => setPickerOpen(false)}
        cvs={cvs}
        loading={cvsLoading}
        selectedCvId={sourceCvId}
        onSelect={onSourceCvChange}
      />
    </div>
  );
}
